if (!customElements.get('chic-model-slideshow')) {
  class ChicModelSlideshow extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.controller) return;
        this.images = Array.from(this.querySelectorAll('.chic-model-slideshow__image'));
        this.controls = this.querySelector('.chic-model-slideshow__controls');
        this.toggle = this.querySelector('[data-model-toggle]');
        this.dots = Array.from(this.querySelectorAll('[data-model-slide]'));
        this.status = this.querySelector('[data-model-status]');
        if (this.images.length < 2 || !this.toggle || !this.controls) return;
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.manualPaused = this.dataset.autoplay !== 'true' || this.motion.matches;
        this.index = 0;
        this.requestId = 0;
        this.visible = true;
        this.interval = Math.max(3000, Number(this.dataset.interval) || 5000);
        this.images.forEach((image, index) => {
          image.classList.toggle('is-current', index === 0);
          image.setAttribute('aria-hidden', String(index !== 0));
        });
        this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === 0)));
        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.show(Number(dot.dataset.modelSlide), true), options);
        });
        this.toggle.addEventListener('click', () => {
          this.requestId += 1;
          this.manualPaused = !this.manualPaused;
          this.updateToggle();
          this.schedule();
        }, options);
        this.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          if (!event.target.matches('[data-model-slide]')) return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const index = (this.index + direction + this.images.length) % this.images.length;
          this.dots[index].focus();
          this.show(index, true);
        }, options);
        document.addEventListener('visibilitychange', () => this.schedule(), options);
        this.motion.addEventListener('change', () => {
          if (this.motion.matches) {
            this.requestId += 1;
            this.manualPaused = true;
          }
          this.updateToggle();
          this.schedule();
        }, options);
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            this.visible = entries[0].isIntersecting;
            this.schedule();
          });
          this.observer.observe(this);
        }
        this.classList.add('is-ready');
        this.controls.hidden = false;
        this.updateToggle();
        this.schedule();
      });
    }

    disconnectedCallback() {
      this.requestId += 1;
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }

    updateToggle() {
      const label = this.manualPaused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause;
      this.toggle.setAttribute('aria-label', label);
      this.toggle.setAttribute('title', label);
      this.toggle.querySelector('[data-model-play]').toggleAttribute('hidden', !this.manualPaused);
      this.toggle.querySelector('[data-model-pause]').toggleAttribute('hidden', this.manualPaused);
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.isConnected || this.manualPaused || !this.visible || document.hidden) return;
      this.timer = setTimeout(() => this.show((this.index + 1) % this.images.length), this.interval);
    }

    async show(index, announce = false) {
      clearTimeout(this.timer);
      const requestId = ++this.requestId;
      const image = this.images[index];
      if (!image) return;
      if (!image.complete) {
        image.loading = 'eager';
        try {
          await image.decode();
        } catch {
          if (requestId === this.requestId) this.schedule();
          return;
        }
      }
      if (requestId !== this.requestId || !this.isConnected) return;
      if (image.naturalWidth > 0) {
        this.index = index;
        this.images.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-current', slideIndex === index);
          slide.setAttribute('aria-hidden', String(slideIndex !== index));
        });
        this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === index)));
        if (announce) this.status.textContent = this.status.dataset.template.replace('[current]', String(index + 1));
      }
      this.schedule();
    }
  }

  customElements.define('chic-model-slideshow', ChicModelSlideshow);
}
